import React, { useState } from 'react';
import { AlertTriangle, ArrowRight, Zap } from 'lucide-react';

export type EscalationChoice = 'quick_action' | 'critical_problem';

export interface QuickActionPayload {
  whatToDo: string;
  assignee: string;
}

export interface CriticalProblemPayload {
  summary: string;
  assignee: string;
}

interface NonConformityEscalationPanelProps {
  rotinaTitle: string;
  members: Array<{ id: string; name: string }>;
  defaultAssigneeId?: string;
  saving?: boolean;
  onCancel: () => void;
  onSubmitQuickAction: (payload: QuickActionPayload) => void;
  onSubmitCriticalProblem: (payload: CriticalProblemPayload) => void;
}

/**
 * Fluxo dinâmico após seleção △ (Não Conforme):
 * Ação Rápida → Central de Ações | Problema Crítico → MASP.
 */
export const NonConformityEscalationPanel: React.FC<NonConformityEscalationPanelProps> = ({
  rotinaTitle,
  members,
  defaultAssigneeId,
  saving = false,
  onCancel,
  onSubmitQuickAction,
  onSubmitCriticalProblem,
}) => {
  const [choice, setChoice] = useState<EscalationChoice | null>(null);
  const [whatToDo, setWhatToDo] = useState('');
  const [assignee, setAssignee] = useState(defaultAssigneeId ?? '');

  const canSubmit = whatToDo.trim().length >= 3 && assignee.trim().length > 0 && !saving;

  if (!choice) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-4 dark:border-amber-800/60 dark:bg-amber-950/30">
        <div className="mb-3 flex items-start gap-2">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div>
            <p className="text-[13px] font-bold text-amber-900 dark:text-amber-100">Não conformidade registrada</p>
            <p className="text-[11px] text-amber-800/80 dark:text-amber-200/70">
              {rotinaTitle} — escolha como tratar o desvio:
            </p>
          </div>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setChoice('quick_action')}
            className="flex flex-col items-start gap-1 rounded-lg border border-slate-200 bg-white p-3 text-left transition-colors hover:border-blue-300 hover:bg-blue-50/50 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-blue-700 dark:hover:bg-blue-950/20"
          >
            <span className="flex items-center gap-1.5 text-[12px] font-bold text-slate-800 dark:text-slate-100">
              <Zap size={14} className="text-blue-500" />
              Abrir Ação Rápida
            </span>
            <span className="text-[10px] text-slate-500 dark:text-slate-400">
              2 campos: o que fazer e quem executa → Central de Ações
            </span>
          </button>
          <button
            type="button"
            onClick={() => setChoice('critical_problem')}
            className="flex flex-col items-start gap-1 rounded-lg border border-red-200 bg-white p-3 text-left transition-colors hover:border-red-300 hover:bg-red-50/50 dark:border-red-900/50 dark:bg-slate-900 dark:hover:border-red-700 dark:hover:bg-red-950/20"
          >
            <span className="flex items-center gap-1.5 text-[12px] font-bold text-slate-800 dark:text-slate-100">
              <AlertTriangle size={14} className="text-red-500" />
              Disparar Problema Crítico
            </span>
            <span className="text-[10px] text-slate-500 dark:text-slate-400">
              Envia para Central de Problemas (MASP) com SLA e Cadeia de Ajuda
            </span>
          </button>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="mt-3 text-[11px] font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400"
        >
          Voltar
        </button>
      </div>
    );
  }

  const isCritical = choice === 'critical_problem';

  return (
    <div
      className={`rounded-xl border p-4 ${
        isCritical
          ? 'border-red-200 bg-red-50/60 dark:border-red-900/50 dark:bg-red-950/25'
          : 'border-blue-200 bg-blue-50/60 dark:border-blue-900/50 dark:bg-blue-950/25'
      }`}
    >
      <p className="mb-3 text-[12px] font-bold text-slate-800 dark:text-slate-100">
        {isCritical ? 'Problema Crítico (MASP)' : 'Ação Rápida'}
      </p>

      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        {isCritical ? 'Descrição do desvio' : 'O que fazer'}
      </label>
      <textarea
        value={whatToDo}
        onChange={(e) => setWhatToDo(e.target.value)}
        rows={2}
        placeholder={isCritical ? 'Descreva o desvio crítico observado…' : 'Ação imediata para corrigir…'}
        className="mb-3 w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-[13px] dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-100"
      />

      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        Quem executa
      </label>
      <select
        value={assignee}
        onChange={(e) => setAssignee(e.target.value)}
        className="mb-4 h-10 w-full rounded-lg border border-slate-200 px-3 text-[13px] dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-100"
      >
        <option value="">Selecione…</option>
        {members.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name}
          </option>
        ))}
      </select>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setChoice(null)}
          disabled={saving}
          className="rounded-lg px-3 py-2 text-[12px] font-medium text-slate-600 hover:bg-white/60 dark:text-slate-300"
        >
          Voltar
        </button>
        <button
          type="button"
          disabled={!canSubmit}
          onClick={() => {
            const payload = { whatToDo: whatToDo.trim(), assignee };
            if (isCritical) onSubmitCriticalProblem(payload);
            else onSubmitQuickAction(payload);
          }}
          className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[12px] font-semibold text-white disabled:opacity-40 ${
            isCritical ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {saving ? 'Enviando…' : isCritical ? 'Abrir no MASP' : 'Criar Ação'}
          {!saving && <ArrowRight size={14} />}
        </button>
      </div>
    </div>
  );
};
